import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent } from '@/lib/analytics';
import { submitToHubSpot, getHubSpotCookie, getClientIP, initHubSpotTracking } from '@/lib/hubspot';
import { getLeadSourceFields } from '@/lib/lead-source';
import { linkAnonymousTracking } from '@/lib/unit-interest';

interface EnquiryRailProps {
  heroRef: React.RefObject<HTMLElement>;
  isAuthenticated: boolean;
  passwordProtectionEnabled: boolean;
}

const SESSION_STORAGE_KEY = 'pearl_enquiry_widget_dismissed';

export const EnquiryRail = ({ heroRef, isAuthenticated, passwordProtectionEnabled }: EnquiryRailProps) => {
  const [loading, setLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    // Check sessionStorage on mount
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
    }
    return false;
  });
  const [heroExitedViewport, setHeroExitedViewport] = useState(false);
  const [formLoaded, setFormLoaded] = useState(false);
  const [hasShownWidget, setHasShownWidget] = useState(false);
  const widgetRef = useRef<HTMLDivElement>(null);
  const formDataRef = useRef({
    name: '',
    email: '',
    phone: '',
    interest: '',
  });
  const [formData, setFormData] = useState(formDataRef.current);

  // IntersectionObserver to detect when hero exits viewport
  useEffect(() => {
    if (!heroRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Hero has exited viewport when isIntersecting becomes false
          if (!entry.isIntersecting) {
            setHeroExitedViewport(true);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      }
    );

    observer.observe(heroRef.current);

    return () => {
      observer.disconnect();
    };
  }, [heroRef]);

  // Check if widget should be shown
  const shouldShowWidget = 
    heroExitedViewport && 
    !isDismissed &&
    (!passwordProtectionEnabled || (passwordProtectionEnabled && isAuthenticated));

  // Lazy load form assets when widget should appear
  useEffect(() => {
    if (shouldShowWidget && !formLoaded) {
      // Initialize HubSpot tracking when widget appears
      initHubSpotTracking();
      setFormLoaded(true);
    }
  }, [shouldShowWidget, formLoaded]);

  // Track widget shown event (only once)
  useEffect(() => {
    if (shouldShowWidget && !hasShownWidget) {
      trackEvent('enquiry_widget_shown', {});
      setHasShownWidget(true);
    }
  }, [shouldShowWidget, hasShownWidget]);

  // Keyboard accessibility: Escape key handler
  useEffect(() => {
    if (!shouldShowWidget) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isMinimized) {
          handleDismiss();
        } else {
          handleMinimize();
        }
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [shouldShowWidget, isMinimized]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    trackEvent('lead_started', { source: 'enquiry_widget', interest: formData.interest });
    
    // HubSpot-ready form data structure
    const hubspotCookie = getHubSpotCookie();
    const clientIP = await getClientIP();
    const leadSourceFields = getLeadSourceFields();
    
    // Split name into first and last name
    const nameParts = formData.name.trim().split(/\s+/);
    const firstName = nameParts[0] || formData.name;
    const lastName = nameParts.slice(1).join(' ') || '';

    const hubspotData = {
      fields: [
        { name: 'firstname', value: firstName },
        { name: 'lastname', value: lastName },
        { name: 'email', value: formData.email },
        { name: 'phone', value: formData.phone },
        { name: 'interest', value: formData.interest },
        // Include lead source data (UTM parameters, referrer, landing page)
        ...leadSourceFields,
      ],
      context: {
        pageUri: typeof window !== 'undefined' ? window.location.href : '',
        pageName: typeof document !== 'undefined' ? document.title : '',
        ...(hubspotCookie ? { hutk: hubspotCookie } : {}),
        ...(clientIP ? { ipAddress: clientIP } : {}),
      },
    };

    // Submit to HubSpot API
    const result = await submitToHubSpot(hubspotData);
    
    // Store contact email in sessionStorage for future tracking
    if (typeof window !== 'undefined' && formData.email) {
      sessionStorage.setItem('hubspot_contact_email', formData.email);
    }

    // Link anonymous tracking to contact email (don't wait - fire and forget)
    if (formData.email) {
      linkAnonymousTracking(formData.email, formData.phone).catch(err => {
        console.error('Error linking anonymous tracking:', err);
      });
    }
    
    setLoading(false);
    
    if (result.success) {
      toast.success('Thank you! We\'ll be in touch soon.', {
        description: 'Check your email for more information about The Pearl.',
      });
      
      trackEvent('lead_qualified', { source: 'enquiry_widget' });
      trackEvent('enquiry_submit', { interest: formData.interest });
      
      setFormData({ name: '', email: '', phone: '', interest: '' });
      formDataRef.current = { name: '', email: '', phone: '', interest: '' };
    } else {
      // Fallback: show success message even if HubSpot fails (graceful degradation)
      // In production, you might want to log this error to your error tracking service
      console.error('HubSpot submission error:', result.error);
      
      toast.success('Thank you! We\'ll be in touch soon.', {
        description: 'Check your email for more information about The Pearl.',
      });
      
      trackEvent('lead_qualified', { source: 'enquiry_widget' });
      trackEvent('enquiry_submit', { interest: formData.interest });
      
      setFormData({ name: '', email: '', phone: '', interest: '' });
      formDataRef.current = { name: '', email: '', phone: '', interest: '' };
    }
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    trackEvent('enquiry_widget_minimized', {});
  };

  const handleExpand = () => {
    setIsMinimized(false);
    trackEvent('enquiry_widget_expanded', {});
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
    }
    trackEvent('enquiry_widget_dismissed', {});
  };

  // Don't render widget if conditions aren't met
  if (!shouldShowWidget) return null;

  // Minimized state: show small floating button
  if (isMinimized) {
    return (
      <div
        ref={widgetRef}
        className="hidden lg:block fixed bottom-24 right-6 z-40"
        role="complementary"
        aria-label="Quick Enquiry Widget"
      >
        <Button
          onClick={handleExpand}
          variant="luxury"
          className="shadow-lg hover:shadow-xl transition-all duration-300"
          aria-label="Expand enquiry form"
          aria-expanded="false"
        >
          <ChevronUp className="h-4 w-4 mr-2" />
          Quick Enquiry
        </Button>
      </div>
    );
  }

  // Expanded state: show full form
  return (
    <div
      ref={widgetRef}
      className="hidden lg:block fixed bottom-24 right-6 z-40 w-80 max-w-sm"
      role="complementary"
      aria-label="Quick Enquiry Form"
    >
      <Card className="shadow-luxury border-brand-teal/20 animate-in slide-in-from-bottom-4 duration-300">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-display text-xl">Quick Enquiry</CardTitle>
              <p className="text-sm text-muted-foreground">
                Get started in 30 seconds
              </p>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleMinimize}
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                aria-label="Minimize enquiry form"
                aria-expanded="true"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDismiss}
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                aria-label="Close enquiry form"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* Hidden fields for HubSpot tracking */}
            <input type="hidden" name="hubspotutk" value={getHubSpotCookie()} />
            <input type="hidden" name="pageUri" value={typeof window !== 'undefined' ? window.location.href : ''} />
            <input type="hidden" name="pageName" value={typeof document !== 'undefined' ? document.title : ''} />

            <div>
              <Label htmlFor="rail-name">Name</Label>
              <Input
                id="rail-name"
                value={formData.name}
                onChange={(e) => {
                  const newData = { ...formData, name: e.target.value };
                  setFormData(newData);
                  formDataRef.current = newData;
                }}
                placeholder="Your full name"
                required
                aria-required="true"
                autoComplete="name"
              />
            </div>
            
            <div>
              <Label htmlFor="rail-email">Email</Label>
              <Input
                id="rail-email"
                type="email"
                value={formData.email}
                onChange={(e) => {
                  const newData = { ...formData, email: e.target.value };
                  setFormData(newData);
                  formDataRef.current = newData;
                }}
                placeholder="you@example.com"
                required
                aria-required="true"
                autoComplete="email"
              />
            </div>
            
            <div>
              <Label htmlFor="rail-phone">Phone</Label>
              <Input
                id="rail-phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => {
                  const newData = { ...formData, phone: e.target.value };
                  setFormData(newData);
                  formDataRef.current = newData;
                }}
                placeholder="+1 (345) 123-4567"
                required
                aria-required="true"
                autoComplete="tel"
              />
            </div>
            
            <div>
              <Label htmlFor="rail-interest">I'm interested in</Label>
              <Select 
                value={formData.interest} 
                onValueChange={(value) => {
                  const newData = { ...formData, interest: value };
                  setFormData(newData);
                  formDataRef.current = newData;
                }}
              >
                <SelectTrigger id="rail-interest" aria-required="true">
                  <SelectValue placeholder="Select option" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-Bedroom Residence">1-Bedroom Residence</SelectItem>
                  <SelectItem value="2-Bedroom Residence">2-Bedroom Residence</SelectItem>
                  <SelectItem value="3-Bedroom Residence">3-Bedroom Residence</SelectItem>
                  <SelectItem value="Investment Opportunity">Investment Opportunity</SelectItem>
                  <SelectItem value="Commercial Space">Commercial Space</SelectItem>
                  <SelectItem value="Lagoon View">Lagoon View</SelectItem>
                  <SelectItem value="Canal View">Canal View</SelectItem>
                  <SelectItem value="General Information">General Information</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button type="submit" variant="luxury" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  Sending...
                </>
              ) : (
                'Submit Enquiry'
              )}
            </Button>
            
            <p className="text-xs text-muted-foreground text-center">
              By submitting, you agree to be contacted about The Pearl.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

